import { component } from 'picoapp';
import getProductItemsFromClerkResponse from 'utils/get-product-items-from-clerk-response';
import { clear } from 'utils/node';
import { ClerkResponse } from '../../../typescript/Clerk';

interface SearchConfig {
  pageSize: number
  query: string
}

export default component(async (node) => {
  const heading = node.querySelector('[data-heading]');
  const productOutput = node.querySelector('[data-product-output]');
  const paginationInfo = node.querySelector<HTMLElement>('[data-pagination-info]');
  const paginationText = node.querySelector('[data-pagination-text]');
  const paginationFill = node.querySelector<HTMLElement>('[data-pagination-fill]');
  const viewMoreBtn = node.querySelector<HTMLButtonElement>('[data-view-more-btn]');

  const config: SearchConfig = JSON.parse(node.dataset.config);
  const { pageSize, query } = config;

  const url = new URL(window.location.href);
  let currentPage = parseInt(url.searchParams.get('page'), 10) || 1;

  let hits: number;

  const searchStrings = theme.strings.search;

  const getPage = async (page: number) => {
    if (!Clerk) {
      console.error('Clerk not found on window. Please check that Clerk is enabled in theme settings'); // eslint-disable-line no-console
      return '';
    }

    const offset = page <= 1 ? 1 : ((page - 1) * pageSize) + 1;

    const productResponse = await new Promise<ClerkResponse>((resolve) => {
      Clerk(
        'call',
        'search/search',
        {
          query,
          offset,
          labels: ['Search page'],
          limit: pageSize,
          attributes: ['handle'],
        },
        async (clerkResponse: ClerkResponse) => {
          resolve(clerkResponse);
        },
        (clerkResponse: ClerkResponse) => {
          console.error(clerkResponse.message); // eslint-disable-line no-console
        },
      );
    });

    const products = await getProductItemsFromClerkResponse(productResponse, 'product-item');

    if (!hits) hits = products.length;

    const productItems = products.length ? products.map((html) => `<li>${html}</li>`).join('') : '';

    return productItems;
  };

  const updatePaginationInfo = () => {
    const pagesTotal = hits / pageSize;
    const productsViewed = currentPage < pagesTotal ? currentPage * pageSize : hits;

    const paginationTextTranslation = hits > 1
      ? searchStrings.productsViewedPlural
      : searchStrings.productsViewedSingle;

    paginationText.innerHTML = paginationTextTranslation
      .replace('%count%', productsViewed.toString())
      .replace('%total%', hits.toString());

    const percentageViewed = Math.floor((productsViewed / hits) * 100);
    paginationFill.style.width = `${percentageViewed}%`;
  };

  const getInitialProducts = async () => {
    const pagesToGet = [];

    for (let i = 1; i <= currentPage; i += 1) {
      pagesToGet.push(i);
    }

    const productItemArrays = await Promise.all(pagesToGet.map((page) => getPage(page)));
    const productItems = productItemArrays.flat();

    clear(productOutput);

    if (productItems.length) {
      productOutput.innerHTML = productItems.join('');
    } else {
      heading.textContent = searchStrings.noResults.replace('%terms%', query);
      productOutput.remove();
    }

    updatePaginationInfo();

    if (currentPage * pageSize >= hits) {
      viewMoreBtn.remove();
    } else {
      viewMoreBtn.hidden = false;
    }

    paginationInfo.hidden = !(hits > 0);

    Clerk('click', '*[data-clerk-product-id]');
  };

  const getNextPage = async () => {
    currentPage += 1;

    url.searchParams.set('page', currentPage.toString());
    window.history.replaceState({}, '', url);

    const productItems = await getPage(currentPage);

    productOutput.insertAdjacentHTML('beforeend', productItems);

    updatePaginationInfo();

    Clerk('click', '*[data-clerk-product-id]');
  };

  const initViewMoreBtn = () => {
    if (!viewMoreBtn) return;

    viewMoreBtn.addEventListener('click', async () => {
      const html = viewMoreBtn.innerHTML;
      const btnWidth = viewMoreBtn.getBoundingClientRect().width;

      viewMoreBtn.style.width = `${btnWidth}px`;
      viewMoreBtn.innerHTML = theme.icons.loading;
      viewMoreBtn.disabled = true;

      await getNextPage();

      if (currentPage * pageSize >= hits) {
        viewMoreBtn.remove();
      } else {
        viewMoreBtn.style.width = '';
        viewMoreBtn.innerHTML = html;
        viewMoreBtn.disabled = false;
      }
    });
  };

  const init = () => {
    getInitialProducts();
    initViewMoreBtn();
  };

  init();
});
